import { useEffect, useRef, useState, useCallback } from "react";
import { Scribe, RealtimeEvents } from "@elevenlabs/client";
// ============= Hook Implementation =============
export function useScribe(options = {}) {
    const { 
    // Callbacks
    onSessionStarted, onPartialTranscript, onCommittedTranscript, onCommittedTranscriptWithTimestamps, onError, onAuthError, onQuotaExceededError, onCommitThrottledError, onTranscriberError, onUnacceptedTermsError, onRateLimitedError, onInputError, onQueueOverflowError, onResourceExhaustedError, onSessionTimeLimitExceededError, onChunkSizeExceededError, onInsufficientAudioActivityError, onConnect, onDisconnect, 
    // Connection options
    token: defaultToken, modelId: defaultModelId, baseUri: defaultBaseUri, commitStrategy: defaultCommitStrategy, vadSilenceThresholdSecs: defaultVadSilenceThresholdSecs, vadThreshold: defaultVadThreshold, minSpeechDurationMs: defaultMinSpeechDurationMs, minSilenceDurationMs: defaultMinSilenceDurationMs, languageCode: defaultLanguageCode, 
    // Mode options
    microphone: defaultMicrophone, audioFormat: defaultAudioFormat, sampleRate: defaultSampleRate, 
    // Auto-connect
    autoConnect = false, 
    // Timestamps
    includeTimestamps: defaultIncludeTimestamps, 
    // Keyterms and verbatim control
    keyterms: defaultKeyterms, noVerbatim: defaultNoVerbatim, } = options;
    const connectionRef = useRef(null);
    const [status, setStatus] = useState("disconnected");
    const [isMuted, setIsMuted] = useState(false);
    const [partialTranscript, setPartialTranscript] = useState("");
    const [committedTranscripts, setCommittedTranscripts] = useState([]);
    const [error, setError] = useState(null);
    // Cleanup on unmount
    useEffect(() => {
        return () => {
            connectionRef.current?.close();
        };
    }, []);
    const connect = useCallback(async (runtimeOptions = {}) => {
        if (connectionRef.current) {
            console.warn("Already connected");
            return;
        }
        try {
            setStatus("connecting");
            setError(null);
            // Merge default options with runtime options
            const token = runtimeOptions.token || defaultToken;
            const modelId = runtimeOptions.modelId || defaultModelId;
            if (!token) {
                throw new Error("Token is required");
            }
            if (!modelId) {
                throw new Error("Model ID is required");
            }
            // Determine mode: microphone or manual
            const microphone = runtimeOptions.microphone || defaultMicrophone;
            const audioFormat = runtimeOptions.audioFormat || defaultAudioFormat;
            const sampleRate = runtimeOptions.sampleRate || defaultSampleRate;
            let connection;
            // Include timestamps if explicitly requested OR if the callback is provided
            const includeTimestamps = runtimeOptions.includeTimestamps ??
                defaultIncludeTimestamps ??
                !!(runtimeOptions.onCommittedTranscriptWithTimestamps ||
                    onCommittedTranscriptWithTimestamps);
            if (microphone) {
                // Microphone mode
                connection = Scribe.connect({
                    token,
                    modelId,
                    baseUri: runtimeOptions.baseUri || defaultBaseUri,
                    commitStrategy: runtimeOptions.commitStrategy || defaultCommitStrategy,
                    vadSilenceThresholdSecs: runtimeOptions.vadSilenceThresholdSecs ||
                        defaultVadSilenceThresholdSecs,
                    vadThreshold: runtimeOptions.vadThreshold || defaultVadThreshold,
                    minSpeechDurationMs: runtimeOptions.minSpeechDurationMs || defaultMinSpeechDurationMs,
                    minSilenceDurationMs: runtimeOptions.minSilenceDurationMs ||
                        defaultMinSilenceDurationMs,
                    languageCode: runtimeOptions.languageCode || defaultLanguageCode,
                    keyterms: runtimeOptions.keyterms || defaultKeyterms,
                    noVerbatim: runtimeOptions.noVerbatim ?? defaultNoVerbatim,
                    microphone,
                    includeTimestamps,
                });
            }
            else if (audioFormat && sampleRate) {
                // Manual audio mode
                connection = Scribe.connect({
                    token,
                    modelId,
                    baseUri: runtimeOptions.baseUri || defaultBaseUri,
                    commitStrategy: runtimeOptions.commitStrategy || defaultCommitStrategy,
                    vadSilenceThresholdSecs: runtimeOptions.vadSilenceThresholdSecs ||
                        defaultVadSilenceThresholdSecs,
                    vadThreshold: runtimeOptions.vadThreshold || defaultVadThreshold,
                    minSpeechDurationMs: runtimeOptions.minSpeechDurationMs || defaultMinSpeechDurationMs,
                    minSilenceDurationMs: runtimeOptions.minSilenceDurationMs ||
                        defaultMinSilenceDurationMs,
                    languageCode: runtimeOptions.languageCode || defaultLanguageCode,
                    keyterms: runtimeOptions.keyterms || defaultKeyterms,
                    noVerbatim: runtimeOptions.noVerbatim ?? defaultNoVerbatim,
                    includeTimestamps,
                    audioFormat,
                    sampleRate,
                });
            }
            else {
                throw new Error("Either microphone options or (audioFormat + sampleRate) must be provided");
            }
            connectionRef.current = connection;
            // Set up event listeners
            connection.on(RealtimeEvents.SESSION_STARTED, () => {
                setStatus("connected");
                onSessionStarted?.();
            });
            connection.on(RealtimeEvents.PARTIAL_TRANSCRIPT, (data) => {
                const message = data;
                setPartialTranscript(message.text);
                setStatus("transcribing");
                onPartialTranscript?.(message);
            });
            connection.on(RealtimeEvents.COMMITTED_TRANSCRIPT, (data) => {
                const message = data;
                const segment = {
                    id: `${Date.now()}-${Math.random()}`,
                    text: message.text,
                    timestamp: Date.now(),
                    isFinal: true,
                };
                setCommittedTranscripts(prev => [...prev, segment]);
                setPartialTranscript("");
                onCommittedTranscript?.(message);
            });
            connection.on(RealtimeEvents.COMMITTED_TRANSCRIPT_WITH_TIMESTAMPS, (data) => {
                const message = data;
                const segment = {
                    id: `${Date.now()}-${Math.random()}`,
                    text: message.text,
                    timestamp: Date.now(),
                    isFinal: true,
                    words: message.words,
                };
                setCommittedTranscripts(prev => [...prev, segment]);
                setPartialTranscript("");
                onCommittedTranscriptWithTimestamps?.(message);
            });
            connection.on(RealtimeEvents.ERROR, (err) => {
                const message = err;
                setError(message.error);
                setStatus("error");
                onError?.(new Error(message.error));
            });
            connection.on(RealtimeEvents.AUTH_ERROR, (data) => {
                const message = data;
                setError(message.error);
                setStatus("error");
                onAuthError?.(message);
            });
            connection.on(RealtimeEvents.QUOTA_EXCEEDED, (data) => {
                const message = data;
                setError(message.error);
                setStatus("error");
                onQuotaExceededError?.(message);
            });
            connection.on(RealtimeEvents.COMMIT_THROTTLED, (data) => {
                const message = data;
                setError(message.error);
                setStatus("error");
                onCommitThrottledError?.(message);
            });
            connection.on(RealtimeEvents.TRANSCRIBER_ERROR, (data) => {
                const message = data;
                setError(message.error);
                setStatus("error");
                onTranscriberError?.(message);
            });
            connection.on(RealtimeEvents.UNACCEPTED_TERMS, (data) => {
                const message = data;
                setError(message.error);
                setStatus("error");
                onUnacceptedTermsError?.(message);
            });
            connection.on(RealtimeEvents.RATE_LIMITED, (data) => {
                const message = data;
                setError(message.error);
                setStatus("error");
                onRateLimitedError?.(message);
            });
            connection.on(RealtimeEvents.INPUT_ERROR, (data) => {
                const message = data;
                setError(message.error);
                setStatus("error");
                onInputError?.(message);
            });
            connection.on(RealtimeEvents.QUEUE_OVERFLOW, (data) => {
                const message = data;
                setError(message.error);
                setStatus("error");
                onQueueOverflowError?.(message);
            });
            connection.on(RealtimeEvents.RESOURCE_EXHAUSTED, (data) => {
                const message = data;
                setError(message.error);
                setStatus("error");
                onResourceExhaustedError?.(message);
            });
            connection.on(RealtimeEvents.SESSION_TIME_LIMIT_EXCEEDED, (data) => {
                const message = data;
                setError(message.error);
                setStatus("error");
                onSessionTimeLimitExceededError?.(message);
            });
            connection.on(RealtimeEvents.CHUNK_SIZE_EXCEEDED, (data) => {
                const message = data;
                setError(message.error);
                setStatus("error");
                onChunkSizeExceededError?.(message);
            });
            connection.on(RealtimeEvents.INSUFFICIENT_AUDIO_ACTIVITY, (data) => {
                const message = data;
                setError(message.error);
                setStatus("error");
                onInsufficientAudioActivityError?.(message);
            });
            connection.on(RealtimeEvents.OPEN, () => {
                onConnect?.();
            });
            connection.on(RealtimeEvents.CLOSE, () => {
                setStatus("disconnected");
                setIsMuted(false);
                connectionRef.current = null;
                onDisconnect?.();
            });
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to connect";
            setError(errorMessage);
            setStatus("error");
            throw err;
        }
    }, [
        defaultToken,
        defaultModelId,
        defaultBaseUri,
        defaultCommitStrategy,
        defaultVadSilenceThresholdSecs,
        defaultVadThreshold,
        defaultMinSpeechDurationMs,
        defaultMinSilenceDurationMs,
        defaultLanguageCode,
        defaultMicrophone,
        defaultAudioFormat,
        defaultSampleRate,
        defaultIncludeTimestamps,
        defaultKeyterms,
        defaultNoVerbatim,
        onSessionStarted,
        onPartialTranscript,
        onCommittedTranscript,
        onCommittedTranscriptWithTimestamps,
        onError,
        onAuthError,
        onQuotaExceededError,
        onCommitThrottledError,
        onTranscriberError,
        onUnacceptedTermsError,
        onRateLimitedError,
        onInputError,
        onQueueOverflowError,
        onResourceExhaustedError,
        onSessionTimeLimitExceededError,
        onChunkSizeExceededError,
        onInsufficientAudioActivityError,
        onConnect,
        onDisconnect,
    ]);
    const disconnect = useCallback(() => {
        connectionRef.current?.close();
        connectionRef.current = null;
        setStatus("disconnected");
        setIsMuted(false);
    }, []);
    const mute = useCallback(() => {
        if (!connectionRef.current) {
            throw new Error("Not connected to Scribe");
        }
        connectionRef.current.mute();
        setIsMuted(true);
    }, []);
    const unmute = useCallback(() => {
        if (!connectionRef.current) {
            throw new Error("Not connected to Scribe");
        }
        connectionRef.current.unmute();
        setIsMuted(false);
    }, []);
    const sendAudio = useCallback((audioBase64, options) => {
        if (!connectionRef.current) {
            throw new Error("Not connected to Scribe");
        }
        connectionRef.current.send({ audioBase64, ...options });
    }, []);
    const commit = useCallback(() => {
        if (!connectionRef.current) {
            throw new Error("Not connected to Scribe");
        }
        connectionRef.current.commit();
    }, []);
    const clearTranscripts = useCallback(() => {
        setCommittedTranscripts([]);
        setPartialTranscript("");
    }, []);
    const getConnection = useCallback(() => {
        return connectionRef.current;
    }, []);
    // Auto-connect if enabled
    useEffect(() => {
        if (autoConnect) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- autoConnect intentionally starts the connection when the hook mounts
            connect();
        }
    }, [autoConnect, connect]);
    return {
        // State
        status,
        isConnected: status === "connected" || status === "transcribing",
        isTranscribing: status === "transcribing",
        isMuted,
        partialTranscript,
        committedTranscripts,
        error,
        // Methods
        connect,
        disconnect,
        mute,
        unmute,
        sendAudio,
        commit,
        clearTranscripts,
        getConnection,
    };
}
// Export types and enums from client for convenience
export { AudioFormat, CommitStrategy, RealtimeEvents, } from "@elevenlabs/client";
//# sourceMappingURL=scribe.js.map